import { ButtonHTMLAttributes, MouseEventHandler } from 'react';
import { ThemeColor } from 'styled-components';
import { IconListType } from '../Icon';

export enum ButtonType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  SECONDARY_BLUE = 'secondaryBlue',
  TERTIARY = 'tertiary',
  SECONDARY_TEXT = 'secondaryText',
  PRIMARY_TEXT = 'primaryText',
  PRIMARY_DESTRUCTIVE = 'primaryDestructive',
  SECONDARY_DESTRUCTIVE = 'secondaryDestructive',
  TEXT_DESTRUCTIVE = 'textDestructive',
  PRIMARY_GREEN = 'primaryGreen',
}

export type ButtonTypeProperties = {
  background: ThemeColor;
  border?: ThemeColor;
  hover: ThemeColor;
  active?: ThemeColor;
  content: ThemeColor;
  boxShadow?: string;
};

export type ButtonTypeStyles = Record<ButtonType, ButtonTypeProperties>;

export type ButtonStyles = {
  backgroundColor: string;
  hoverColor: string;
  activeColor: string;
  borderColor?: string;
  boxShadow?: string;
  contentColor: string;
  fontSize: string;
  height: string;
  pointerEvents: string;
  margin?: string;
  padding: string;
  width?: string;
  minWidth: string;
};

export type ButtonSize = 'mini' | 'regular';

export interface ButtonSizeProperties {
  padding: string;
  height: string;
  fontSize: string;
  iconSize: string;
}

export type ButtonSizeStyles = Record<ButtonSize, ButtonSizeProperties>;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The type of button.
   * Determines the color of the button.
   *
   * Ensure to import the ButtonType and use as ex: ButtonType.SECONDARY.
   */
  btnType?: ButtonType;
  /**
   * Color value (hex/rgb) of completed icon that only gets displayed if the prop `completed` is true. If no color is passed in, it defaults to the loading color of the `btnType` style.
   */
  completedIconColor?: string;
  completed?: boolean;
  /**
   * Color value (hex/rgb) of failure icon that only gets displayed if the prop `failed` is true. If no color is passed in, it defaults to the loading color of the `btnType` style.
   */
  failedIconColor?: string;
  failed?: boolean;
  /**
   * When using this prop, consider setting the width property so
   * the button doesn't change widths when changing from loading state to not.
   */
  isLoading?: boolean;
  mini?: boolean;
  minWidth?: string;
  disabled?: boolean;
  margin?: string;
  width?: string;
  dataId?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** Used for react testing library. Avoid using. Use aria attributes or roles instead to access the button. */
  'data-testid'?: string;
  iconName?: IconListType;
  /**
   * Renders the button with only the icon and no text.
   * Requires the `iconName` prop to be set.
   * Make sure to add an `aria-label` to describe the button, by default it will use the icon name.
   * If a standalone icon is needed, use `ClickableIcon`.
   */
  iconOnly?: boolean;
}
